import ResponsiveImage from "../components/common/ResponsiveImage";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Send, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { fmtMoney, titleCase } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/permissions";

const CHECK_LABELS = {
  clear_title: "Clear title",
  accurate_location: "Accurate location",
  good_photos: "Good photos",
  clear_inclusions_exclusions: "Clear inclusions/exclusions",
  cancellation_policy_present: "Cancellation policy present",
  pricing_present: "Pricing present",
  availability_configured: "Availability configured",
  no_prohibited_content: "No prohibited content",
  safety_accessibility_info: "Safety/accessibility info included",
};

export default function ExperienceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [exp, setExp] = useState(null);
  const [slots, setSlots] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [savingQuality, setSavingQuality] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const loadVersion = useRef({ version: 0 });

  const load = useCallback(async () => {
    const requestState = loadVersion.current;
    const version = ++requestState.version;
    setLoadError(false);
    try {
      const { data } = await api.get(`/experiences/${id}`);
      if (version !== requestState.version) return;
      const product = data.product;
      const options = product.options.filter(option => option.is_active);
      setExp({
        ...data.summary,
        description: product.full_description || product.short_description,
        photos: product.photo_urls,
        inclusions: [...product.inclusions_tags, ...(product.inclusions_other ? [product.inclusions_other] : [])],
        exclusions: [...product.not_included_tags, ...(product.not_included_other ? [product.not_included_other] : [])],
        what_to_bring: product.guest_must_know ? [product.guest_must_know] : [],
        cancellation_policy: data.cancellation_policy,
        meeting_point: [...new Set(options.map(option => option.meeting_point_location).filter(Boolean))].join("; "),
        languages: [...new Set(options.flatMap(option => [...option.languages, ...(option.languages_other ? [option.languages_other] : [])]))],
        quality_checklist: data.quality_checklist,
        moderation_reason: data.moderation_reason,
      });
    } catch {
      if (version !== requestState.version) return;
      setExp(null);
      setLoadError(true);
      toast.error("Failed to load experience");
    }
  }, [id]);
  useEffect(() => {
    const requestState = loadVersion.current;
    setExp(null); load();
    return () => { requestState.version++; };
  }, [load]);
  // Scoped reads avoid loading unrelated reviews or relying on dummy global APIs.
  useEffect(() => {
    let active = true;
    setSlots(null); setReviews(null);
    api.get(`/experiences/${id}/availability`).then(({ data }) => { if (active) setSlots(data); })
      .catch(() => { if (active) { setSlots(false); toast.error("Failed to load availability"); } });
    api.get(`/experiences/${id}/reviews`).then(({ data }) => { if (active) setReviews(data); })
      .catch(() => { if (active) { setReviews(false); toast.error("Failed to load reviews"); } });
    return () => { active = false; };
  }, [id, refresh]);

  const doAction = async (action, { reason }) => {
    const requestState = loadVersion.current;
    const version = requestState.version;
    try {
      await api.post(`/experiences/${id}/${action}`, { reason, note: reason });
      if (version !== requestState.version) return;
      toast.success(`Experience ${action}`);
      await load();
      setRefresh(value => value + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const toggleQuality = async (key, checked) => {
    if (!can(user, "experience.edit") || savingQuality) return;
    setSavingQuality(true);
    try {
      const updated = { ...exp.quality_checklist, [key]: checked };
      await api.patch(`/experiences/${id}`, { quality_checklist: updated });
      setExp(current => current?.id === id ? { ...current, quality_checklist: updated } : current);
    } catch {
      toast.error("Failed to update checklist");
    } finally {
      setSavingQuality(false);
    }
  };

  if (!exp) return <div className="py-10 text-sm text-muted-foreground" data-testid="experience-loading">{loadError ? "Unable to load experience." : "Loading…"}</div>;
  const canPublish = can(user, "experience.publish");
  const canUnpublish = can(user, "experience.unpublish");

  return (
    <div data-testid="experience-detail-page">
      <button onClick={() => nav("/experiences")} className="mt-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-experiences">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to experiences
      </button>
      <PageHeader
        title={exp.title}
        description={[exp.supplier_name, exp.destination, exp.category].filter(Boolean).join(" · ")}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={exp.status} />
            {canUnpublish && exp.status === "published" && (
              <Button size="sm" variant="destructive" onClick={() => setDialog({ action: "unpublish", title: "Unpublish experience?", confirm: "Unpublish", reasonRequired: true, destructive: true })} data-testid="exp-unpublish-btn"><EyeOff className="w-4 h-4" /> Unpublish</Button>
            )}
            {canPublish && (exp.status === "draft" || exp.status === "unpublished") && (
              <Button size="sm" onClick={() => setDialog({ action: "publish", title: "Publish experience?", confirm: "Publish" })} data-testid="exp-publish-btn"><Send className="w-4 h-4" /> Publish</Button>
            )}
          </div>
        }
      />

      <Tabs defaultValue="listing" className="mt-6">
        <TabsList className="bg-transparent border-b border-border w-full justify-start rounded-none h-auto p-0 gap-0">
          {["listing", "pricing", "availability", "rules", "reviews", "quality"].map((t) => (
            <TabsTrigger key={t} value={t} className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-sm capitalize" data-testid={`exp-tab-${t}`}>
              {t === "listing" ? "Listing content" : t === "rules" ? "Booking rules" : t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="listing" className="pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <div className="grid grid-cols-3 gap-2">
                {exp.photos?.map((p, i) => (
                  <ResponsiveImage imageWidth={960} key={i} src={p} alt="" className="aspect-[4/3] w-full object-cover rounded-md border border-border" />
                ))}
              </div>
              <div className="bg-card border border-border rounded-lg p-5">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground">Description</h3>
                <p className="text-sm mt-2 leading-relaxed">{exp.description}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ListBlock title="Inclusions" items={exp.inclusions} />
                <ListBlock title="Exclusions" items={exp.exclusions} />
                <ListBlock title="Guest information" items={exp.what_to_bring} />
                <div className="bg-card border border-border rounded-lg p-5">
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Cancellation policy</h3>
                  <p className="text-sm">{exp.cancellation_policy}</p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <InfoCard title="Details">
                <Row label="Destination" value={exp.destination} />
                <Row label="Meeting point" value={exp.meeting_point} />
                <Row label="Duration" value={exp.duration_hours == null ? "Varies by option" : `${exp.duration_hours} hours`} />
                <Row label="Languages" value={(exp.languages || []).join(", ")} />
              </InfoCard>
              <InfoCard title="Supplier">
                <Row label="Name" value={<Link to={`/suppliers/${exp.supplier_id}`} className="hover:underline">{exp.supplier_name}</Link>} />
              </InfoCard>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pricing" className="pt-6">
          <InfoCard title="Pricing">
            <Row label="Price from" value={fmtMoney(exp.price_from, exp.currency)} />
            <Row label="Currency" value={exp.currency} />
            <Row label="Duration" value={exp.duration_hours == null ? "Varies by option" : `${exp.duration_hours} hours`} />
          </InfoCard>
        </TabsContent>

        <TabsContent value="availability" className="pt-6">
          <div className="border border-border rounded-lg bg-card overflow-hidden">
            <p className="px-4 py-3 text-xs text-muted-foreground">Next 30 days, up to 60 scheduled slots. Remaining capacity is an estimate based on the option limit and recorded guests.</p>
            <table className="w-full text-sm">
              <thead><tr className="bg-muted/30 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <th className="text-left px-4 py-2 font-medium">Date</th>
                <th className="text-left px-4 py-2 font-medium">Time</th>
                <th className="text-left px-4 py-2 font-medium">Capacity</th>
                <th className="text-left px-4 py-2 font-medium">Booked</th>
                <th className="text-left px-4 py-2 font-medium">Available</th>
                <th className="text-left px-4 py-2 font-medium">Price</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
              </tr></thead>
              <tbody>
                {(Array.isArray(slots) ? slots : []).map((s) => {
                  const avail = s.available;
                  const soldOut = avail != null && avail <= 0;
                  return (
                    <tr key={s.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-2.5 font-mono text-xs">{s.date}<div className="text-muted-foreground">{s.option_name}</div></td>
                      <td className="px-4 py-2.5 font-mono text-xs">{s.start_time}</td>
                      <td className="px-4 py-2.5 tabular-nums">{s.capacity ?? "—"}</td>
                      <td className="px-4 py-2.5 tabular-nums">{s.booked}</td>
                      <td className="px-4 py-2.5 tabular-nums">{avail ?? "—"}</td>
                      <td className="px-4 py-2.5 tabular-nums">{fmtMoney(s.price, s.currency)}</td>
                      <td className="px-4 py-2.5">
                        <StatusBadge status={soldOut ? "warning" : s.is_available ? "approved" : "unpublished"} label={soldOut ? "Sold out" : s.is_available ? "Open" : "Closed"} variant={soldOut ? "warning" : s.is_available ? "success" : "muted"} />
                      </td>
                    </tr>
                  );
                })}
                {(!Array.isArray(slots) || slots.length === 0) && <tr><td colSpan={7} className="px-4 py-8 text-center text-xs text-muted-foreground">{slots === null ? "Loading availability…" : slots === false ? "Unable to load availability." : "No configured slots in the next 30 days."}</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="rules" className="pt-6">
          <InfoCard title="Booking rules">
            {exp.moderation_reason && <Row label="Last moderation reason" value={exp.moderation_reason} />}
            <Row label="Cancellation policy" value={exp.cancellation_policy} />
            <Row label="Meeting point" value={exp.meeting_point} />
            <Row label="Languages" value={(exp.languages || []).join(", ")} />
          </InfoCard>
        </TabsContent>

        <TabsContent value="reviews" className="pt-6">
          <div className="space-y-3">
            {(Array.isArray(reviews) ? reviews : []).map((r) => (
              <div key={r.id} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-medium">{r.customer_name} · <span className="tabular-nums">{r.rating}/5</span></div>
                  <StatusBadge status={r.status} />
                </div>
                <p className="text-sm text-muted-foreground">{r.text}</p>
              </div>
            ))}
            {(!Array.isArray(reviews) || reviews.length === 0) && <div className="text-sm text-muted-foreground">{reviews === null ? "Loading reviews…" : reviews === false ? "Unable to load reviews." : "No reviews yet."}</div>}
          </div>
        </TabsContent>

        <TabsContent value="quality" className="pt-6">
          <div className="bg-card border border-border rounded-lg p-5 space-y-2">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Quality checklist</h3>
            {Object.entries(CHECK_LABELS).map(([key, label]) => (
              <label key={key} className="flex items-center gap-3 py-1.5 cursor-pointer" data-testid={`quality-${key}`}>
                <Checkbox
                  checked={!!exp.quality_checklist?.[key]}
                  onCheckedChange={(v) => toggleQuality(key, v)}
                  disabled={!can(user, "experience.edit") || savingQuality}
                />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={!!dialog}
        onOpenChange={() => setDialog(null)}
        title={dialog?.title}
        confirmLabel={dialog?.confirm}
        requireReason={dialog?.reasonRequired}
        destructive={dialog?.destructive}
        onConfirm={(payload) => doAction(dialog.action, payload)}
      />
    </div>
  );
}

function ListBlock({ title, items }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{title}</h3>
      <ul className="text-sm space-y-1 list-disc pl-4 marker:text-muted-foreground">
        {(items || []).map((i, idx) => <li key={idx}>{i}</li>)}
        {(!items || items.length === 0) && <li className="list-none text-muted-foreground">—</li>}
      </ul>
    </div>
  );
}

function InfoCard({ title, children }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">{title}</h3>
      <dl className="space-y-2">{children}</dl>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-xs text-muted-foreground shrink-0">{label}</dt>
      <dd className="text-sm text-right break-all">{value || "—"}</dd>
    </div>
  );
}
